import React, { useCallback, useId, useMemo, useRef, useState } from 'react'

import { HeadlessListbox } from 'slab/components/HeadlessListbox'
import { Popper } from 'slab/components/Popper'

import { AutocompleteContext } from './context'
import { AutocompleteInput } from './Input'
import { AutocompleteList } from './List'
import { AutocompleteOption } from './Option'
import { AutocompleteStatus } from './Status'

export interface AutocompleteProps {
  children: React.ReactNode;
  /**
   * Callback when the open state changes. Useful for delaying API calls until
   * the user interacts with the autocomplete input.
   */
  onOpenChange?: (open: boolean) => void;
}

// React overrides the setter for `value` so we need to use
// `Object.getOwnPropertyDescriptor` to get the original setter
function setValue (input: HTMLInputElement, value: string) {
  const setValue = Object?.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )?.set

  setValue?.call(input, value)
}

export function Autocomplete ({
  children,
  onOpenChange
}: AutocompleteProps) {
  const [open, _setOpen] = useState(false)
  const [statusElement, setStatusElement] = useState<HTMLDivElement | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const setOpen = useCallback(
    (open: boolean) => {
      _setOpen(open)
      onOpenChange?.(open)
    },
    [onOpenChange]
  )

  const onSelect = useCallback((selectedValue: string | null, { closeAfterSelection = true } = {}) => {
    if (inputRef.current && selectedValue !== null) {
      setValue(inputRef.current, selectedValue)
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (closeAfterSelection) {
      setOpen(false)
    }
  }, [])

  const contextValue = useMemo(() => ({
    inputRef,
    listId,
    open,
    onSelect,
    setOpen,
    statusElement
  }), [listId, onSelect, open, statusElement])

  return (
    <Popper>
      <HeadlessListbox>
        <div
          className='sr-only'
          role='status'
          aria-live='polite'
          aria-atomic='true'
          ref={(element) => element && setStatusElement(element)}
        />
        <AutocompleteContext.Provider value={contextValue}>
          {children}
        </AutocompleteContext.Provider>
      </HeadlessListbox>
    </Popper>
  )
}

Autocomplete.Input = AutocompleteInput
Autocomplete.List = AutocompleteList
Autocomplete.Option = AutocompleteOption
Autocomplete.Status = AutocompleteStatus
